import React from 'react'
import clsx from 'clsx'
import { isEqual } from 'lodash'
import PropTypes from 'prop-types'
import { Vacancy } from 'components'
import { motion, AnimatePresence } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Container, Columns, Column, Field } from 'bloomer'

// Motion Variants
import * as motionVariants from './motion.variants'

// Styles
import styles from './style.module.scss'
import ensureBasePath from '../../../lib/ensureBasePath'
import { trackFor } from 'lib/tracker/utils'

const SelectField = ({
  name,
  items,
  onSelect,
  onToggle,
  labelKey,
  valueKey,
  isActive,
  value: propValue,
}) => {
  const selectedValue = propValue || items[0][valueKey]
  const value = items.find(item => item[valueKey] === selectedValue)

  const handleSelect = value => () => {
    onSelect(name, value)
  }

  return (
    <Field>
      <div className={clsx('select', styles.select, styles.select_desktop)}>
        <div
          className={clsx('select-stylized', styles.select_stylized, isActive ? 'is-active' : '')}
          style={{ zIndex: isActive ? 155 : 'unset' }}>
          <div
            className={clsx('current-filter', styles.current_filter)}
            onClick={() => onToggle(name)}>
            {!!value && value[labelKey]}
          </div>
          <div className={clsx('option-list', styles.option_list)}>
            {items && items.length
              ? sortLabel(items, labelKey).map((item, key) => (
                  <button
                    key={key}
                    type="button"
                    className={styles.option_button}
                    value={item[valueKey]}
                    onClick={handleSelect(item[valueKey])}>
                    {item[labelKey]}
                  </button>
                ))
              : null}
          </div>
        </div>
      </div>
    </Field>
  )
}

function sortLabel(items, labelKey) {
  return items.sort((a, b) => {
    return a[labelKey] > b[labelKey] ? 1 : -1
  })
}

export const List = ({
  jobs,
  types,
  values,
  locations,
  totalJobs,
  isLoading,
  departments,
  activeField,
  jobsPerPage,
  currentPage,
  mobileContent,
  onToggleField,
  handleChangePage,
  handleApplyFilters,
}) => {
  // const isDesktop = useMediaQuery({ query: '(min-width: 768px)' })
  // const isMobile = useMediaQuery({ query: '(max-width: 767px)' })

  const [filter, setFilter] = React.useState(values)
  const inputRef = React.useRef(null)

  React.useEffect(() => {
    if (!isEqual(values, filter)) setFilter(values)
  }, [values])

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0,
  })

  const handleSelect = (name, value) => {
    const filters = { ...filter, [name]: value }

    onToggleField(null)
    handleApplyFilters(filters)
  }

  const handleInputChange = ({ keyCode, target }) => {
    const { value, name } = target
    inputRef.current = target
    setFilter({ ...filter, [name]: value })
    if (keyCode === 13) {
      inputRef.current.blur()
    }
  }

  const handleInputBlur = ({ target }) => {
    const { value, name } = target

    handleApplyFilters({ ...filter, [name]: value })

    onToggleField(null)

    trackFor({
      gtm: {
        event: 'job_search',
        job_query: value,
      },
    })
  }

  const totalPage = Math.ceil(totalJobs / jobsPerPage)

  return (
    <div className={clsx('section', styles.section)} ref={ref}>
      <motion.div
        variants={motionVariants.container}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}>
        <Container>
          <div className={styles.section_wrapper}>
            <div className={styles.form_wrapper}>
              <motion.div variants={motionVariants.child}>
                <Columns>
                  <Column isSize={{ default: 8 }}>
                    <Columns>
                      <Column isSize="1/3">
                        <SelectField
                          labelKey="name"
                          valueKey="department_id"
                          name="department"
                          value={filter.department}
                          isActive={activeField === 'department'}
                          items={[{ department_id: '', name: 'All Department' }, ...departments]}
                          onToggle={onToggleField}
                          onSelect={handleSelect}
                        />
                      </Column>

                      <Column isSize="1/3">
                        <SelectField
                          labelKey="label"
                          valueKey="id"
                          name="location"
                          value={filter.location}
                          isActive={activeField === 'location'}
                          items={[{ id: '', label: 'All Location' }, ...locations]}
                          onToggle={onToggleField}
                          onSelect={handleSelect}
                        />
                      </Column>

                      <Column isSize="1/3">
                        <SelectField
                          labelKey="label"
                          valueKey="id"
                          name="job_type"
                          value={filter.job_type}
                          isActive={activeField === 'job_type'}
                          items={[{ id: '', label: 'All Types' }, ...types]}
                          onToggle={onToggleField}
                          onSelect={handleSelect}
                        />
                      </Column>
                    </Columns>
                  </Column>

                  <Column isSize={{ default: 3 }} isOffset={{ default: 1 }}>
                    <div
                      className={clsx(
                        styles.offcanvas_filter,
                        'offcanvas-filter',
                        activeField === 'mobile' && 'is-active',
                      )}>
                      <div className={styles.offcanvas_container}>
                        <button
                          type="button"
                          className={styles.toggle_mobile}
                          onClick={() => onToggleField(null)}>
                          <small>CLOSE</small>
                        </button>

                        <h3 className={styles.toggle_title}>FILTER</h3>

                        <div className={clsx('select', styles.select, styles.select_mobile)}>
                          <select
                            tabIndex="1"
                            className={styles.select_input}
                            value={filter.department}
                            name="department"
                            onChange={handleInputChange}>
                            <option value="">All Department</option>
                            {departments.length
                              ? departments.map((item, key) => (
                                  <option key={key} value={item.department_id}>
                                    {item.name}
                                  </option>
                                ))
                              : null}
                          </select>
                        </div>

                        <div className={clsx('select', styles.select, styles.select_mobile)}>
                          <select
                            tabIndex="-1"
                            className={styles.select_input}
                            value={filter.location}
                            name="location"
                            onChange={handleInputChange}>
                            <option value="">All Location</option>
                            {locations.length
                              ? locations.map((item, key) => (
                                  <option key={key} value={item.id}>
                                    {item.label}
                                  </option>
                                ))
                              : null}
                          </select>
                        </div>

                        <div className={clsx('select', styles.select, styles.select_mobile)}>
                          <select
                            tabIndex="-1"
                            className={styles.select_input}
                            value={filter.job_type}
                            name="job_type"
                            onChange={handleInputChange}>
                            <option value="">Job Type</option>
                            {types.length
                              ? types.map((item, key) => (
                                  <option key={key} value={item.id}>
                                    {item.label}
                                  </option>
                                ))
                              : null}
                          </select>
                        </div>

                        <button
                          type="button"
                          className={styles.apply}
                          onClick={() => {
                            onToggleField(null)
                            handleApplyFilters(filter)
                          }}>
                          Apply Filter
                        </button>
                      </div>
                    </div>

                    <div
                      className={
                        activeField === 'search'
                          ? clsx(styles.filter_mobile, styles.is_active)
                          : clsx(styles.filter_mobile)
                      }>
                      <Field className={styles.field_text}>
                        <div className={clsx('text', styles.text)}>
                          <input
                            tabIndex="-1"
                            className={clsx('input', styles.text_input)}
                            onKeyUp={handleInputChange}
                            onBlur={handleInputBlur}
                            name="search"
                            type="text"
                            onFocus={() => onToggleField('search')}
                            placeholder="Search where you fit in here"
                          />
                        </div>
                      </Field>

                      <button
                        type="button"
                        className={styles.toggle_mobile}
                        onClick={() => onToggleField('mobile')}>
                        <img
                          className={styles.toggle_image}
                          src={ensureBasePath('/images/icons/filter-open.svg')}
                          alt=""
                        />
                      </button>
                    </div>
                  </Column>
                </Columns>
              </motion.div>
            </div>

            {!!mobileContent && <div className="vacancy-content-mobile">{mobileContent}</div>}

            <div className={styles.result_wrapper}>
              <AnimatePresence exitBeforeEnter>
                <motion.div
                  variants={motionVariants.listContainer}
                  key={isLoading}
                  initial="hidden"
                  animate="visible"
                  exit="hidden">
                  <ul className={styles.result_list}>
                    {isLoading &&
                      Array.from(Array(5).keys()).map(i => (
                        <li key={i} className={styles.result_item}>
                          <Vacancy.Skeleton />
                        </li>
                      ))}

                    {!isLoading && jobs !== undefined && jobs.length ? (
                      jobs.map((job, key) => (
                        <motion.li
                          key={key}
                          variants={motionVariants.listChild}
                          className={styles.result_item}>
                          <Vacancy.Item index={key} data={job.content} id={job.id} key={job.id} />
                        </motion.li>
                      ))
                    ) : !isLoading ? (
                      <li className={`${styles.result_item} ${styles.result_item_no_data}`}>
                        <div className={styles.image_wrapper}>
                          <img
                            className={styles.image}
                            src={ensureBasePath('/images/icons/no-result.svg')}
                            alt=""
                          />
                        </div>

                        <div className={styles.text_wrapper}>
                          <h4 className={styles.text_title}>Sorry....</h4>
                          <p className={styles.text_body}>
                            {`The vacancy you are looking for isn't here, `}
                            <br className="d-none d-md-block" />
                            please try another keywords or broaden your search.
                          </p>
                        </div>
                      </li>
                    ) : null}
                  </ul>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          <motion.div variants={motionVariants.child} className={styles.pagination_wrapper}>
            <ul className={clsx('pagination-list', styles.pagination_list)}>
              {!isLoading && jobs !== undefined && jobs.length
                ? Array.from(Array(totalPage).keys()).map(i => (
                    <li key={i} className={currentPage === i + 1 ? 'active' : ''}>
                      <button
                        type="button"
                        onClick={() => currentPage !== i + 1 && handleChangePage(i + 1)}
                        className={styles.pagination_button}>
                        <span>{i}</span>
                      </button>
                    </li>
                  ))
                : null}
            </ul>

            <ul className={styles.navigation_list}>
              {!!totalPage && currentPage > 1 ? (
                <li>
                  <button
                    type="button"
                    onClick={() => handleChangePage(currentPage - 1)}
                    className={styles.prev_button}>
                    Prev
                  </button>
                </li>
              ) : null}
              {!!totalPage && currentPage < totalPage ? (
                <li>
                  <button
                    type="button"
                    onClick={() => handleChangePage(currentPage + 1)}
                    className={styles.next_button}>
                    Next
                  </button>
                </li>
              ) : null}
            </ul>
          </motion.div>
        </Container>
      </motion.div>
    </div>
  )
}

List.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  jobs: PropTypes.array.isRequired,
  values: PropTypes.object.isRequired,
  departments: PropTypes.array.isRequired,
  locations: PropTypes.array.isRequired,
  types: PropTypes.array.isRequired,
  totalJobs: PropTypes.number,
  handleApplyFilters: PropTypes.func,
}

List.defaultProps = {
  totalJobs: 0,
  handleApplyFilters: () => {},
}

export default List
